import React, { useContext, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import Header from './components/Layout/Header';
import Footer from './components/Layout/Footer';
import LoginModal from './components/shared/LoginModal';
import Home from './pages/Home';
import Menu from './pages/Menu';
import Accommodation from './pages/Accommodation';
import Events from './pages/Events';
import EventBooking from './pages/EventBooking';
import Cart from './pages/Cart';
import Checkout from './pages/Checkout';
import Booking from './pages/Booking';
import Login from './pages/Login';
import Signup from './pages/Signup';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import Profile from './pages/Profile';
import MyOrders from './pages/Customer/MyOrders';
import MyBookings from './pages/Customer/MyBookings';
import PaymentConfirmation from './pages/PaymentConfirmation';
import { AuthContext, AuthProvider } from './context/AuthContext';
import CartProvider from './context/CartContext';
import BookingProvider from './context/BookingContext';

// Admin Components
import AdminLayout from './components/Admin/AdminLayout';
import AdminDashboard from './pages/Admin/Dashboard';
import AdminIndex from './pages/Admin/index';
import OrdersManagement from './pages/Admin/OrdersManagement';
import BookingsManagement from './pages/Admin/BookingsManagement';
import MenuManagement from './pages/Admin/MenuManagement';
import PaymentsManagement from './pages/Admin/PaymentsManagement';
import UserManagement from './pages/Admin/UserManagement';
import AnalyticsDashboard from './pages/Admin/AnalyticsDashboard';
import { isLakehouseContext } from './utils/restaurantContext';

import './styles/index.css';

const createCircularIconDataUrl = (src, size = 64) => new Promise((resolve, reject) => {
  const image = new Image();

  image.onload = () => {
    const canvas = document.createElement('canvas');
    canvas.width = size;
    canvas.height = size;

    const ctx = canvas.getContext('2d');
    if (!ctx) {
      reject(new Error('Canvas context is not available'));
      return;
    }

    const radius = size / 2;
    ctx.clearRect(0, 0, size, size);

    ctx.save();
    ctx.beginPath();
    ctx.arc(radius, radius, radius, 0, Math.PI * 2);
    ctx.closePath();
    ctx.clip();
    ctx.drawImage(image, 0, 0, size, size);
    ctx.restore();

    resolve(canvas.toDataURL('image/png'));
  };

  image.onerror = () => reject(new Error('Failed to load icon image'));
  image.src = src;
});

const AppContent = () => {
  const { user } = useContext(AuthContext);
  const location = useLocation();

  useEffect(() => {
    if (user) {
      return;
    }

    if (location.pathname === '/lakehouse') {
      localStorage.setItem('selected_restaurant_id', '2');
      return;
    }

    if (location.pathname === '/') {
      localStorage.setItem('selected_restaurant_id', '1');
    }
  }, [location.pathname, user]);

  useEffect(() => {
    const isLakehouseView = isLakehouseContext(user, location.pathname);
    const title = isLakehouseView
      ? 'Casa Gatchalian Lakehouse - Restaurant Bar & Cafe'
      : 'Casa Gatchalian - Restaurant Bar & Cafe';
    const iconPath = isLakehouseView ? '/images/casagatchalianlakehouse_logo.png' : '/images/casatablogo.png?v=5';

    document.title = title;

    const ensureLink = (rel, href) => {
      let link = document.querySelector(`link[rel="${rel}"]`);
      if (!link) {
        link = document.createElement('link');
        link.rel = rel;
        document.head.appendChild(link);
      }
      link.href = href;
      if (rel === 'icon') {
        link.type = 'image/png';
      }
    };

    let cancelled = false;

    const applyIcons = async () => {
      try {
        const circularIcon = await createCircularIconDataUrl(iconPath, 64);
        if (cancelled) {
          return;
        }

        ensureLink('icon', circularIcon);
        ensureLink('apple-touch-icon', circularIcon);
      } catch (error) {
        if (cancelled) {
          return;
        }

        ensureLink('icon', iconPath);
        ensureLink('apple-touch-icon', iconPath);
      }
    };

    applyIcons();

    return () => {
      cancelled = true;
    };
  }, [location.pathname, user]);

  return (
    <Routes>
      {/* Admin Routes (No Header/Footer) */}
      <Route path="/admin" element={<AdminLayout />}>
        <Route index element={<AdminDashboard />} />
        <Route path="dashboard" element={<AdminDashboard />} />
        <Route path="menu" element={<MenuManagement />} />
        <Route path="orders" element={<OrdersManagement />} />
        <Route path="bookings" element={<BookingsManagement />} />
        <Route path="payments" element={<PaymentsManagement />} />
        <Route path="users" element={<UserManagement />} />
        <Route path="analytics" element={<AnalyticsDashboard />} />
        <Route path="settings" element={<AdminIndex />} />
      </Route>

      {/* Public Routes with Header/Footer */}
      <Route
        path="*"
        element={
          <div className="min-h-screen flex flex-col">
            <Header />
            <LoginModal />
            <main className="flex-1">
              <Routes>
                <Route path="/" element={<Home />} />
                <Route path="/lakehouse" element={<Home />} />
                <Route path="/menu" element={<Menu />} />
                <Route path="/accommodation" element={<Accommodation />} />
                <Route path="/booking" element={<Booking />} />
                <Route path="/events" element={<Events />} />
                <Route path="/event-booking" element={<EventBooking />} />
                <Route path="/payment-confirmation" element={<PaymentConfirmation />} />
                <Route path="/cart" element={<Cart />} />
                <Route path="/checkout" element={<Checkout />} />
                <Route path="/login" element={<Login />} />
                <Route path="/signup" element={<Signup />} />
                <Route path="/forgot-password" element={<ForgotPassword />} />
                <Route path="/reset-password" element={<ResetPassword />} />
                <Route path="/profile" element={<Profile />} />
                <Route path="/my-orders" element={<MyOrders />} />
                <Route path="/my-bookings" element={<MyBookings />} />
              </Routes>
            </main>
            <Footer />
          </div>
        }
      />
    </Routes>
  );
};

const App = () => {
  return (
    <Router>
      <CartProvider>
        <BookingProvider>
          <AuthProvider>
            <AppContent />
          </AuthProvider>
        </BookingProvider>
      </CartProvider>
    </Router>
  );
};

export default App;